import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Map, LayoutGrid, Layers as LayersIcon } from 'lucide-react'; // Importa le icone necessarie
// Importa l'hook principale, il selettore e i tipi necessari
import useGeoData, { selectAllUserLayers } from '../../store/useGeoData'; // Importato selectAllUserLayers
import type { GeoDataState } from '../../store/types'; // Aggiunto LayerBase
import { useLeftPanelState } from './hooks/useLeftPanelState'; // Importa il nuovo hook
import SaveProjectDialog from './SaveProjectDialog'; // Importa il dialog Salva Progetto

// Importa i componenti Accordion
import { HierarchicalAccordion } from '../../common/HierarchicalAccordion';
import { HierarchicalAccordionItem } from '../../common/HierarchicalAccordionItem';

// Importa i contenuti dei menu
import BasemapMenu from './components/BasemapMenu';
import WidgetMenu from './components/WidgetMenu';
import LayerList from './components/LayerList'; // Importato LayerList
import ProjectActions from './components/ProjectActions';

const LeftPanelContainer: React.FC = () => {
  // Usa il custom hook per gestire lo stato UI
  const {
    isSaveProjectDialogOpen,
    setIsSaveProjectDialogOpen,
    LeftPanelContainerRef,
  } = useLeftPanelState();

  // Recupera azioni necessarie dallo store Zustand
  const loadSessionState = useGeoData((state: GeoDataState) => state.loadSessionState);
  const getStateForSave = useGeoData((state: GeoDataState) => state.getStateForSave);
  const layers = useGeoData(selectAllUserLayers); // Utilizza il nuovo selettore
  const removeLayer = useGeoData((state: GeoDataState) => state.removeLayer);
  const toggleLayerVisibility = useGeoData((state: GeoDataState) => state.toggleLayerVisibility);

  const [activeAccordionItemId, setActiveAccordionItemId] = useState<string | number | null>(null);
  const [accordionKey, setAccordionKey] = useState(0); // Stato per la chiave dell'accordion
  const previousLayersLengthRef = useRef(0); // Inizializza a 0 per gestire l'apertura al primo layer

  useEffect(() => {
    const currentLayersLength = layers.length;

    // Se il numero di layer è aumentato rispetto al riferimento precedente,
    // apri l'accordion dei layer.
    if (currentLayersLength > previousLayersLengthRef.current) {
      setActiveAccordionItemId("left-panel-layers");
      // Incrementa la chiave per forzare il ri-rendering dell'accordion
      // e assicurare che defaultOpenId venga applicato, anche se l'utente
      // aveva chiuso l'accordion manualmente.
      setAccordionKey(prevKey => prevKey + 1);
    }

    // Aggiorna il riferimento alla lunghezza precedente per il prossimo controllo.
    // Questo deve avvenire indipendentemente dal fatto che l'accordion sia stato aperto o meno,
    // per riflettere correttamente lo stato attuale per il *prossimo* cambiamento.
    previousLayersLengthRef.current = currentLayersLength;

    // Dipendenze: layers e le funzioni di impostazione dello stato.
  }, [layers, setActiveAccordionItemId, setAccordionKey]);

  // --- Gestione Salvataggio/Caricamento Progetto (Logica rimane nel contenitore) ---
  const handleSaveProjectConfirm = useCallback((projectName: string) => {
    if (!projectName || projectName.trim() === "") {
      return;
    }
    try {
      const sanitizedProjectName = projectName.trim().replace(/[^a-z0-9_-]/gi, '_');
      const fileName = `${sanitizedProjectName}.json`;
      const stateToSave = getStateForSave();
      const jsonString = JSON.stringify(stateToSave, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore durante il salvataggio del progetto:", error);
      alert("Si è verificato un errore durante il salvataggio del progetto. Controlla la console per i dettagli.");
    } finally {
      setIsSaveProjectDialogOpen(false);
    }
  }, [setIsSaveProjectDialogOpen, getStateForSave]);

  const handleLoadProjectClick = useCallback(() => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.json';
    fileInput.style.display = 'none';

    fileInput.onchange = (event) => {
      const target = event.target as HTMLInputElement;
      if (target.files && target.files.length > 0) {
        const file = target.files[0];
        const reader = new FileReader();

        reader.onload = (e) => {
          const fileContent = e.target?.result;
          if (typeof fileContent === 'string') {
            try {
              const loadedState = JSON.parse(fileContent);
              if (window.confirm("Caricando questo progetto sovrascriverai lo stato corrente. Continuare?")) {
                loadSessionState(loadedState);
              }
            } catch (error) {
              console.error("Errore parsing JSON caricato:", error);
              alert(`Errore nel formato del file JSON "${file.name}". Assicurati che sia un file JSON valido.`);
            }
          } else {
             alert(`Impossibile leggere il contenuto del file "${file.name}".`);
          }
          target.value = '';
        };

        reader.onerror = () => {
          alert(`Si è verificato un errore durante la lettura del file "${file.name}".`);
          target.value = '';
        };

        reader.readAsText(file);
      } else {
         target.value = '';
      }
      document.body.removeChild(fileInput);
    };

    document.body.appendChild(fileInput);
    fileInput.click();
  }, [loadSessionState]);

  // --- JSX Componente ---
  return (
    <div ref={LeftPanelContainerRef}
      style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* Contenitore Interno per Menu */}
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          padding: 'var(--spacing-ml) var(--spacing-xxl)',
          boxSizing: 'border-box',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* --- BLOCCO MENU SUPERIORE CON ACCORDION --- */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--spacing-s)', // Riduciamo il gap se l'accordion ha già padding/margin
          }}
        >
          <HierarchicalAccordion key={accordionKey} defaultOpenId={activeAccordionItemId}>
            <HierarchicalAccordionItem
              id="left-panel-basemaps"
              title="Basemaps"
              level={1}
              icon={Map} // Passa il componente icona, non l'istanza
            >
              <BasemapMenu />
            </HierarchicalAccordionItem>

            <HierarchicalAccordionItem
              id="left-panel-widgets"
              title="Widget"
              level={1}
              icon={LayoutGrid} // Passa il componente icona
            >
              <WidgetMenu />
            </HierarchicalAccordionItem>

            <HierarchicalAccordionItem
              id="left-panel-layers"
              title="Layers"
              level={1}
              icon={LayersIcon} // Passa il componente icona
            >
              <LayerList
                layers={layers}
                onRemoveLayer={removeLayer}
                onToggleVisibility={toggleLayerVisibility}
              />
            </HierarchicalAccordionItem>
          </HierarchicalAccordion>
        </div>

        {/* --- WIDGET ATTIVI --- */}

        {/* --- BLOCCO MENU INFERIORE --- */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--spacing-s)', /* Ridotto gap per i pulsanti inferiori */
          }}
        >
          <ProjectActions
            onOpenSaveDialog={() => setIsSaveProjectDialogOpen(true)}
            onLoadProjectClick={handleLoadProjectClick}
          />
        </div>
      </div>

      {/* --- Render Condizionale del Dialog Salva Progetto --- */}
      {isSaveProjectDialogOpen && (
        <SaveProjectDialog
          onClose={() => setIsSaveProjectDialogOpen(false)}
          onSave={handleSaveProjectConfirm}
        />
      )}
    </div>
  );
};

export default LeftPanelContainer;